import {
  getCustomColDefProperties,
  useBlotterVisibleRowsEnrichment,
  useMultiMarketDataSnapshot,
  useVisibleCellsChanged,
  type KeyedMarketDataSnapshot,
  type MinimalSubscriptionResponse,
  type UseMultiMarketDataSnapshotRequest,
} from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { useMemo } from 'react';
import { map, type Observable } from 'rxjs';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';
import { OrgConfigurationKey, useOrgConfiguration } from '../../../providers/OrgConfigurationProvider';
import type { PriceBlotterEntity } from './PriceBlotterEntity';
import { buildPriceBlotterMarketDataRequests, getPriceBlotterMarketDataRequest } from './utils';
const DEFAULT_MAX_SUBSCRIPTIONS = 80;

function getMarketDataSnapshotKey(mds: KeyedMarketDataSnapshot) {
  return mds.requestKey;
}

function getMDSEnrichmentLookupKey({ item, column }: { item: PriceBlotterEntity; column: Column }) {
  return getPriceBlotterMarketDataRequest(column, item)?.key;
}

function getSecurityKey(s: PriceBlotterEntity) {
  return s.security.Symbol;
}

interface UsePriceBlotterMarketDataEnrichmentParams {
  dataObservable: Observable<MinimalSubscriptionResponse<PriceBlotterEntity>>;
}

export const usePriceBlotterMarketDataEnrichment = ({
  dataObservable,
}: UsePriceBlotterMarketDataEnrichmentParams): {
  gridOptionsOverlay: ReturnType<typeof useVisibleCellsChanged<PriceBlotterEntity>>['gridOptionsOverlay'];
  enrichedDataObservable: Observable<MinimalSubscriptionResponse<PriceBlotterEntity>>;
} => {
  const { getConfig } = useOrgConfiguration();
  const { visibleCellsChangedObs, gridOptionsOverlay } = useVisibleCellsChanged({
    getUniqueKey: getSecurityKey,
    isColumnRelevant: column => getCustomColDefProperties(column.getColDef())?.talosColumnType === 'securityMarketData',
  });

  // Convert the visible cells we receive into unique requests per (symbol, market selections).
  const visibleRequestsObs: Observable<UseMultiMarketDataSnapshotRequest[]> = useMemo(
    () =>
      visibleCellsChangedObs.pipe(
        map(({ visibleColumns, renderedItems }) =>
          buildPriceBlotterMarketDataRequests({
            visibleColumns,
            renderedItems,
          })
        )
      ),
    [visibleCellsChangedObs]
  );

  const maxActiveSubscriptions = getConfig(
    OrgConfigurationKey.PriceGridMaxActiveSubscriptions,
    DEFAULT_MAX_SUBSCRIPTIONS
  );

  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
  const marketDataObs = useMultiMarketDataSnapshot({
    requestsObs: visibleRequestsObs,
    subscriptionTag: 'Price Grid',
    maxActiveSubscriptions,
    showAllInPrices: !!showAllInPrices,
    showFirmLiquidity,
  });

  const { enrichedDataObservable } = useBlotterVisibleRowsEnrichment<PriceBlotterEntity, KeyedMarketDataSnapshot>({
    dataObservable,
    enrichmentObservable: marketDataObs,
    visibleCellsChangedObs,
    getUniqueKey: getSecurityKey,
    getUniqueEnrichmentKey: getMarketDataSnapshotKey,
    getEnrichmentLookupKey: getMDSEnrichmentLookupKey,
  });

  return {
    gridOptionsOverlay,
    enrichedDataObservable,
  };
};
